import { createContext, ReactNode, useContext, useState } from 'react';

interface SwapRecord {
  swapId: string;
  outPlayerId: number;
  inPlayerId: number;
  timestamp: Date;
}

interface SwapHistoryContextType {
  swapHistory: SwapRecord[];
  addSwap: (outPlayerId: number, inPlayerId: number) => void;
  clearSwapHistory: () => void;
  getSwapCount: () => number;
}

const SwapHistoryContext = createContext<Partial<SwapHistoryContextType>>({});

export const useSwapHistory = (): SwapHistoryContextType => {
  const context = useContext(SwapHistoryContext);
  if (!context) {
    throw new Error('useSwapHistory must be used within a SwapHistoryProvider');
  }
  return context as SwapHistoryContextType;
};

export const SwapHistoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [swapHistory, setSwapHistory] = useState<SwapRecord[]>([]);

  const addSwap = (outPlayerId: number, inPlayerId: number) => {
    // Check if this exact swap (same in-out pair) already exists
    const isDuplicate = swapHistory.some(swap =>
      (swap.outPlayerId === outPlayerId && swap.inPlayerId === inPlayerId)
    );

    if (isDuplicate) {
      // Don't add duplicate swap
      return;
    }

    const newSwap: SwapRecord = {
      swapId: `swap-${Date.now()}`,
      outPlayerId,
      inPlayerId,
      timestamp: new Date()
    };

    setSwapHistory(prev => [...prev, newSwap]);
  };

  const clearSwapHistory = () => {
    setSwapHistory([]);
  };

  const getSwapCount = () => {
    return swapHistory.length;
  };

  return (
    <SwapHistoryContext.Provider
      value={{
        swapHistory,
        addSwap,
        clearSwapHistory,
        getSwapCount
      }}
    >
      {children}
    </SwapHistoryContext.Provider>
  );
};